/**
 * EditStudentPhoneButton — June 2026
 * ===================================
 *
 * Single shared UI for editing a student's phone number.  All four
 * call-sites (Students Management, Global Search → Student Detail,
 * Registration Cases → phone-missing rows, and any future surface)
 * import THIS component instead of rolling their own modal — so the
 * UX, validation and audit trail stay identical no matter where the
 * admin clicks the pencil icon.
 *
 * It is a thin wrapper around the canonical endpoint:
 *     POST /api/admin/student-phone/{student_profile_id}
 *           body: { whatsapp_number: "+966XXXXXXXXX" }
 *
 * The endpoint owns the atomic update across `users`,
 * `student_profiles`, and `legacy_registration_requests`, and writes
 * one audit_logs row per affected collection.
 *
 * Props
 * -----
 *  studentProfileId   string  required
 *  currentPhone       string  optional   — pre-filled in the dialog
 *  studentName        string  optional   — shown in the dialog header
 *  onSaved            (resp)  optional   — called with the API response
 *                                          so the parent can refetch
 *  testIdPrefix       string  optional   — used to derive data-testids
 *                                          for the trigger button and
 *                                          dialog controls
 *  iconOnly           bool    optional   — render just the pencil
 *                                          (useful in compact rows)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Pencil, X, Loader2, CheckCircle2 } from 'lucide-react';
import PhoneInput from './PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EditStudentPhoneButton = ({
  studentProfileId,
  currentPhone   = '',
  studentName    = '',
  onSaved,
  testIdPrefix   = 'edit-student-phone',
  iconOnly       = false,
  className      = '',
}) => {
  const [open, setOpen]       = useState(false);
  const [phone, setPhone]     = useState(currentPhone || null);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(null);

  // Re-sync local state every time the dialog opens so a freshly
  // re-fetched `currentPhone` from the parent always wins.
  useEffect(() => {
    if (open) {
      setPhone(currentPhone || null);
      setError(null);
      setSuccess(null);
    }
  }, [open, currentPhone]);

  const submit = async () => {
    if (!phone) {
      setError('رقم الهاتف غير صالح. تأكد من الرمز الدولي وعدد الأرقام.');
      return;
    }
    if (phone === (currentPhone || null)) {
      setError('الرقم الجديد هو نفس الرقم الحالي.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/student-phone/${studentProfileId}`,
        { whatsapp_number: phone },
        { withCredentials: true },
      );
      setSuccess(r.data);
      if (onSaved) onSaved(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'تعذّر تحديث الرقم.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={
          iconOnly
            ? 'p-1 text-[#5C6B8C] hover:text-[#0A1128] hover:bg-[#F5F5F0] rounded transition-colors'
            : `inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-[#E2E4E0] rounded hover:bg-[#F5F5F0] text-[#0A1128] ${className}`
        }
        data-testid={`${testIdPrefix}-btn`}
        title="تعديل رقم الهاتف"
        aria-label="تعديل رقم الهاتف"
      >
        <Pencil size={iconOnly ? 14 : 12} />
        {!iconOnly && <span>تعديل</span>}
      </button>

      {open && (
        <div
          dir="rtl"
          className="fixed inset-0 z-[140] flex items-start justify-center pt-[12vh] px-4"
          data-testid={`${testIdPrefix}-modal`}
          onClick={() => { if (!busy) setOpen(false); }}
        >
          <div className="absolute inset-0 bg-[#0A1128]/70 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md bg-white border border-[#E2E4E0] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E4E0]">
              <h3 className="text-sm font-semibold text-[#0A1128]">
                تعديل رقم الهاتف{studentName ? ` — ${studentName}` : ''}
              </h3>
              <button
                type="button"
                onClick={() => !busy && setOpen(false)}
                className="p-1 hover:bg-[#F5F5F0] rounded"
                data-testid={`${testIdPrefix}-close`}
                disabled={busy}
                aria-label="إغلاق"
              >
                <X size={16} className="text-[#5C6B8C]" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="space-y-1">
                <div className="text-xs text-[#5C6B8C]">الرقم الحالي</div>
                <div
                  className="text-sm font-mono text-[#0A1128] bg-[#F5F5F0] border border-[#E2E4E0] rounded px-3 py-2"
                  data-testid={`${testIdPrefix}-current`}
                >
                  {currentPhone || '— لا يوجد رقم —'}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-[#5C6B8C]">الرقم الجديد</label>
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  testId={`${testIdPrefix}-input`}
                />
                <p className="text-[11px] text-[#5C6B8C]">
                  سيتم تحديث الرقم تلقائياً في جميع الجداول المرتبطة
                  (المستخدم، ملف الطالب، طلب التسجيل القديم) وكتابة سجل
                  audit يوضح من قام بالتعديل ومتى.
                </p>
              </div>

              {error && (
                <div
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
                  data-testid={`${testIdPrefix}-error`}
                >
                  {error}
                </div>
              )}

              {success && (
                <div
                  className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 flex items-start gap-2"
                  data-testid={`${testIdPrefix}-success`}
                >
                  <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
                  <div>
                    تم التحديث بنجاح. الرقم الجديد:{' '}
                    <span className="font-mono">{success.applied_phone}</span>
                    <br />
                    الجداول المحدّثة:{' '}
                    <span className="font-mono">
                      {(success.collections_updated || []).join('، ') || '—'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#E2E4E0] bg-[#F8F8F4]">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="px-4 py-2 text-sm text-[#0A1128] hover:bg-[#F5F5F0] rounded disabled:opacity-50"
                data-testid={`${testIdPrefix}-cancel`}
              >
                {success ? 'إغلاق' : 'إلغاء'}
              </button>
              {!success && (
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy || !phone || phone === currentPhone}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-[#0A1128] text-[#C9A24E] hover:bg-[#1a2342] disabled:opacity-40 disabled:cursor-not-allowed rounded"
                  data-testid={`${testIdPrefix}-save`}
                >
                  {busy && <Loader2 size={14} className="animate-spin" />}
                  حفظ التعديل
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditStudentPhoneButton;
